import { Flex, Stack } from "@/components/layout";
import { Alert, Button } from "@/components/ui";
import {
  BarList,
  KpiCard,
  KpiGrid,
  LoadBoundary,
  Panel,
  TwoCol,
  useHubNavigate,
} from "@/modules/hub-kit/HubKit";
import { countWhere, formatCount, humanize } from "@/modules/hub-kit/format";
import { SEGMENTS, type MarketingOverview } from "../growthApi";

export function MarketingOverviewTab({
  data,
  error,
  loading,
  reload,
}: {
  data: MarketingOverview | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}) {
  return (
    <Stack gap={16}>
      <Flex gap={8}>
        <Button variant="secondary" onClick={reload}>
          Refresh
        </Button>
      </Flex>
      <LoadBoundary loading={loading} error={error} onRetry={reload} hasData={!!data} what="the marketing overview">
        {data ? <Body o={data} /> : null}
      </LoadBoundary>
    </Stack>
  );
}

function Body({ o }: { o: MarketingOverview }) {
  const go = useHubNavigate();
  const campaignsDraft = countWhere(o.notifications, ["draft", "pending_approval"]);
  const campaignsScheduled = countWhere(o.notifications, ["scheduled"]);
  const deliveriesFailed = countWhere(o.deliveries, ["failed", "retrying"]);

  const segmentRows = SEGMENTS.map((s) => {
    const size = o.segments[s.key];
    return {
      key: s.key,
      label: s.label,
      value: size?.eligible ?? 0,
      display: `${formatCount(size?.eligible ?? 0)} reachable`,
    };
  });

  const deliveryRows = o.deliveries.map((d) => ({
    key: `${d.status}-${d.channel}`,
    label: `${humanize(d.status)} · ${d.channel.replaceAll("_", " ")}`,
    value: d.count,
    display: formatCount(d.count),
    tone: (d.status === "failed" ? "danger" : d.status === "delivered" ? "success" : undefined) as
      | "danger"
      | "success"
      | undefined,
  }));

  return (
    <Stack gap={24}>
      <KpiGrid>
        <KpiCard
          label="Reachable players"
          value={formatCount(o.segments.all_players?.eligible ?? 0)}
          hint={`${formatCount(o.promotions_opted_out)} opted out of promotions`}
          onClick={() => go("segments")}
        />
        <KpiCard
          label="Announcements (30 days)"
          value={formatCount(o.broadcasts_30d.count)}
          hint={`${formatCount(o.broadcasts_30d.recipients)} inboxes reached`}
          onClick={() => go("broadcasts")}
        />
        <KpiCard
          label="Live promo codes"
          value={formatCount(o.vouchers.live)}
          hint={`${formatCount(o.vouchers.total)} in total · ${formatCount(o.vouchers.redemptions)} used on confirmed bookings · businesses issue these, HUDDL does not`}
          onClick={() => go("promos")}
        />
        <KpiCard
          label="Campaigns in progress"
          value={formatCount(campaignsDraft + campaignsScheduled)}
          hint={`${campaignsDraft} draft or awaiting approval · ${campaignsScheduled} scheduled`}
          onClick={() => go("campaigns")}
        />
        <KpiCard
          label="Failed deliveries"
          value={formatCount(deliveriesFailed)}
          {...(deliveriesFailed > 0 ? { tone: "danger" as const } : {})}
          hint="Failed or retrying"
          onClick={() => go("campaigns", "deliveries")}
        />
      </KpiGrid>

      <Alert tone="info" title="What reaches people today">
        In-app announcements (Broadcasts) land in Play's inbox immediately. Campaign records under Campaigns are the governed approval trail, but approving one does not put anything in a Play inbox, and push, email and SMS have no provider yet. Referral attribution does not exist, so there is no conversion or affiliate performance to show.
      </Alert>

      <TwoCol>
        <Panel title="Segments" description="Reachable = in the segment and not opted out of promotions.">
          <BarList rows={segmentRows.filter((r) => r.value > 0)} emptyMessage="No one to reach yet. Segments fill as players sign up and book." />
        </Panel>
        <Panel title="Campaign deliveries" description="Delivery records by status and channel.">
          <BarList rows={deliveryRows} emptyMessage="No campaign deliveries yet." />
        </Panel>
      </TwoCol>
    </Stack>
  );
}
